/**
 * FileDropOverlay — wraps children in an OS-file drop target.
 * Shows a full-area overlay while files are dragged over. Ignores non-file
 * drags: dnd-kit internal drags do not carry the 'Files' dataTransfer type,
 * so kanban/calendar/field DnD is unaffected.
 */
import React, { useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import type { SxProps, Theme } from '@mui/material/styles'

function isFileDrag(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files')
}

export function FileDropOverlay({
  enabled = true,
  overlayText = 'Drop files here',
  disabledText = 'Drop is not available',
  onFiles,
  children,
  sx,
}: {
  enabled?: boolean
  overlayText?: string
  disabledText?: string
  onFiles: (files: File[]) => void
  children: React.ReactNode
  sx?: SxProps<Theme>
}) {
  const [dragActive, setDragActive] = useState(false)
  const depth = useRef(0)

  return (
    <Box
      sx={{ position: 'relative', ...sx } as SxProps<Theme>}
      onDragEnter={(e: React.DragEvent) => {
        if (!isFileDrag(e)) return
        e.preventDefault()
        depth.current += 1
        setDragActive(true)
      }}
      onDragOver={(e: React.DragEvent) => {
        if (isFileDrag(e)) e.preventDefault()
      }}
      onDragLeave={(e: React.DragEvent) => {
        if (!isFileDrag(e)) return
        depth.current = Math.max(0, depth.current - 1)
        if (depth.current === 0) setDragActive(false)
      }}
      onDrop={(e: React.DragEvent) => {
        if (!isFileDrag(e)) return
        e.preventDefault()
        depth.current = 0
        setDragActive(false)
        if (!enabled) return
        const files = Array.from(e.dataTransfer.files ?? [])
        if (files.length) onFiles(files)
      }}
    >
      {children}
      {dragActive && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            bgcolor: 'background.paper',
            opacity: 0.92,
            border: '2px dashed',
            borderColor: enabled ? 'primary.main' : 'divider',
            borderRadius: 1,
          }}
        >
          <CloudUploadIcon color={enabled ? 'primary' : 'disabled'} sx={{ fontSize: 40 }} />
          <Typography variant="subtitle1" color={enabled ? 'primary' : 'text.secondary'}>
            {enabled ? overlayText : disabledText}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
